/**
 * useEmergingFormulations — abstracts the data source for emerging differentials,
 * formulation notes, and confidential observations on DD pages.
 *
 * WHY THIS EXISTS
 * ---------------
 * SLaM's workflow has two clinicians recording emerging formulations independently
 * (SSI clinician + Lead clinician), stored in `dataModel.emergingFormulations`
 * on the patient document. TOC's workflow has a single clinician whose data
 * lives in CI section 12 and SSI section 3 (legacy paths).
 *
 * This hook provides a unified interface so DD pages (Review, ADHD, Formulation)
 * can render emerging data without knowing which storage path is in use.
 *
 * RESOLUTION
 * ----------
 * 1. If `emergingFormulations` has populated sub-objects → dual-clinician mode
 *    (SLaM new patients). Each clinician's data includes attribution metadata.
 * 2. Otherwise → single-source fallback from CI section 12 + SSI section 3
 *    (TOC, legacy patients). No per-field attribution available.
 */

import { useMemo } from 'react';

/**
 * @typedef {Object} ClinicianEmergingData
 * @property {string[]} differentials - Condition names under consideration.
 * @property {string} formulation - Working hypothesis / emerging formulation text.
 * @property {string} confidentialNotes - Private clinical observations (not in report).
 * @property {import('firebase/firestore').Timestamp|null} updatedAt - Last update timestamp.
 * @property {string|null} updatedBy - Clinician UID who last updated.
 * @property {string|null} updatedByName - Display name of clinician who last updated.
 * @property {boolean} confirmed - Whether the clinician confirmed their selections.
 */

/**
 * @typedef {Object} EmergingFormulationsResult
 * @property {boolean} isDualClinician - True when both SSI and Lead clinician data exists.
 * @property {ClinicianEmergingData|null} ssiClinician - SSI clinician's emerging data.
 * @property {ClinicianEmergingData|null} leadClinician - Lead clinician's emerging data.
 */

const EMPTY_CLINICIAN_DATA = Object.freeze({
  differentials: [],
  formulation: '',
  confidentialNotes: '',
  updatedAt: null,
  updatedBy: null,
  updatedByName: null,
  confirmed: false,
});

/**
 * Normalises a raw clinician data sub-object from Firestore into a consistent shape,
 * filling in missing fields with safe defaults.
 *
 * @param {Object|null|undefined} raw - Raw sub-object from emergingFormulations.
 * @returns {ClinicianEmergingData}
 */
const normaliseClinicianData = (raw) => {
  if (!raw) return null;
  return {
    differentials: Array.isArray(raw.differentials) ? raw.differentials : [],
    formulation: raw.formulation || '',
    confidentialNotes: raw.confidentialNotes || '',
    updatedAt: raw.updatedAt || null,
    updatedBy: raw.updatedBy || null,
    updatedByName: raw.updatedByName || null,
    confirmed: raw.confirmed === true,
  };
};

/**
 * Hook that resolves emerging formulation data for display on DD pages.
 * Returns attributed dual-clinician data when available, or falls back to
 * the legacy single-source paths (CI section 12 + SSI section 3).
 *
 * @param {Object|null} patientDataModel - The patient's Firestore document.
 * @returns {EmergingFormulationsResult}
 */
const useEmergingFormulations = (patientDataModel) => {
  const emergingFormulations = patientDataModel?.dataModel?.emergingFormulations;
  const ciSection12 = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[12] ||
                      patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.['12'];
  const ssiSection3 = patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.[3] ||
                      patientDataModel?.dataModel?.['3_semiStructuredInterview']?.sections?.['3'];

  return useMemo(() => {
    const ssiRaw = emergingFormulations?.ssiClinician;
    const leadRaw = emergingFormulations?.leadClinician;

    // Dual-clinician mode: at least one sub-object has meaningful data
    // (including confidential notes alone — SLaM SSI clinicians may only leave
    // private observations without differentials or a formulation yet).
    const hasMeaningfulClinicianData = (raw) => !!(
      raw && (
        raw.confirmed
        || raw.differentials?.length > 0
        || raw.formulation
        || raw.confidentialNotes
      )
    );
    const hasDualData = hasMeaningfulClinicianData(ssiRaw) || hasMeaningfulClinicianData(leadRaw);

    if (hasDualData) {
      return {
        isDualClinician: true,
        ssiClinician: normaliseClinicianData(ssiRaw),
        leadClinician: normaliseClinicianData(leadRaw),
      };
    }

    // Single-source fallback: read from CI section 12 + SSI section 3 (TOC / legacy)
    const differentials = ciSection12?.differentialDiagnoses || [];
    const formulation = ciSection12?.clinicianNotes?.['Additional Notes'] || '';
    const confidentialNotes = ssiSection3?.internalCaseNotes || '';

    return {
      isDualClinician: false,
      ssiClinician: null,
      leadClinician: (differentials.length > 0 || formulation || confidentialNotes)
        ? {
            ...EMPTY_CLINICIAN_DATA,
            differentials,
            formulation,
            confidentialNotes,
            confirmed: true,
          }
        : null,
    };
  }, [emergingFormulations, ciSection12, ssiSection3]);
};

export default useEmergingFormulations;
